import React from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowRight } from 'lucide-react';
import Surface from './Surface';
import Sparkline from './Sparkline';
import { cn } from '../../utils/tailwindUtils';

const INTENT_STYLES = {
  neutral: {
    icon: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
    accent: 'text-slate-600 dark:text-slate-300',
    spark: 'text-slate-500',
  },
  positive: {
    icon: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
    accent: 'text-emerald-600 dark:text-emerald-400',
    spark: 'text-emerald-500',
  },
  warning: {
    icon: 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
    accent: 'text-amber-600 dark:text-amber-400',
    spark: 'text-amber-500',
  },
  negative: {
    icon: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
    accent: 'text-rose-600 dark:text-rose-400',
    spark: 'text-rose-500',
  },
  info: {
    icon: 'bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
    accent: 'text-sky-600 dark:text-sky-400',
    spark: 'text-sky-500',
  },
  primary: {
    icon: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400',
    accent: 'text-indigo-600 dark:text-indigo-400',
    spark: 'text-indigo-500',
  },
};

const DeltaTag = ({ delta, goodUp = true }) => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span className="text-[11px] text-muted-foreground">—</span>;
  }
  if (Math.abs(delta) < 0.05) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
        <Minus className="w-3 h-3" /> 0%
      </span>
    );
  }
  const up = delta > 0;
  const good = goodUp ? up : !up;
  const color = good
    ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40'
    : 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40';
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-md', color)}>
      <Icon className="w-3 h-3" />
      {up ? '+' : ''}{Number(delta).toFixed(1)}%
    </span>
  );
};

/**
 * Decision-making KPI card.
 *
 * Props:
 *  - icon:      lucide-react Icon component
 *  - title:     string (small top label)
 *  - value:     formatted string (big)
 *  - subtitle:  string (under value)
 *  - intent:    'neutral'|'positive'|'warning'|'negative'|'info'|'primary'
 *  - delta:     number (percent) for trend badge
 *  - goodUp:    boolean — is "up" good for this metric?
 *  - sparkline: number[] or [{value}] — tiny trend chart
 *  - onClick:   function — triggers drill-down drawer
 *  - footer:    React node — optional extra line
 */
const MetricCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  intent = 'neutral',
  delta,
  goodUp = true,
  sparkline,
  onClick,
  footer,
  className,
}) => {
  const styles = INTENT_STYLES[intent] || INTENT_STYLES.neutral;
  return (
    <Surface
      interactive={!!onClick}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn('group relative', className)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {Icon && (
              <span className={cn('w-7 h-7 rounded-lg flex items-center justify-center shrink-0', styles.icon)}>
                <Icon className="w-4 h-4" />
              </span>
            )}
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground truncate">
              {title}
            </span>
          </div>
          <div className="mt-2.5 text-2xl font-bold text-foreground tracking-tight">
            {value}
          </div>
          {subtitle && (
            <div className="mt-0.5 text-xs text-muted-foreground truncate">{subtitle}</div>
          )}
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          {sparkline && sparkline.length > 0 && (
            <div className={styles.spark}>
              <Sparkline data={sparkline} width={72} height={22} />
            </div>
          )}
          <DeltaTag delta={delta} goodUp={goodUp} />
        </div>
      </div>

      {footer && <div className="mt-3 pt-3 border-t border-border text-xs">{footer}</div>}

      {onClick && (
        <ArrowRight className="absolute bottom-3 right-3 w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </Surface>
  );
};

export default MetricCard;
